import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, AlertTriangle, Target, RefreshCw, Wrench, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JudgmentMode = ({ diagnosticId, onClose, onSubmit }) => {
  const [judgment, setJudgment] = useState({
    evidence_bar: 3,
    severity: 'Medium',
    trust_impact: 3,
    reproducibility: 3,
    actionability: 3,
    confidence_level: 3,
    is_credible: false,
    is_material: false,
    is_urgent: false,
    is_commercially_relevant: false,
    judgment_notes: '',
    reviewed_by: 'Operator'
  });

  const [loading, setLoading] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);

  const fetchDiagnostic = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}`);
      setDiagnostic(response.data);
    } catch (err) {
      console.error('Error fetching diagnostic:', err);
    }
  }, [diagnosticId]);

  const fetchExistingJudgment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/diagnostics/${diagnosticId}/judgment`);
      if (response.data.judgment) {
        setJudgment(response.data.judgment);
      }
    } catch (err) {
      console.error('Error fetching judgment:', err);
    }
  }, [diagnosticId]);

  useEffect(() => {
    fetchDiagnostic();
    fetchExistingJudgment();
  }, [fetchDiagnostic, fetchExistingJudgment]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/diagnostics/${diagnosticId}/judgment`, judgment);
      onSubmit && onSubmit();
      onClose();
    } catch (err) {
      console.error('Error submitting judgment:', err);
      alert('Failed to submit judgment');
    } finally {
      setLoading(false);
    }
  };

  const RatingSlider = ({ label, icon: Icon, value, onChange, description }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#FF6A00]" />
          <span className="font-medium text-sm">{label}</span>
        </div>
        <span className="font-mono text-[#FF6A00] font-bold">{value}/5</span>
      </div>
      <input
        type="range"
        min="1"
        max="5"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-[#1A1A1D] rounded-lg appearance-none cursor-pointer slider"
      />
      <p className="text-xs text-[#A0A0A0]">{description}</p>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#0B0B0D] border border-[#FF6A00]/20 rounded-lg max-w-4xl w-full p-8 my-8"
          data-testid="judgment-mode-modal"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-display font-bold text-[#FF6A00] mb-1">
                QA Radar Judgment Mode
              </h2>
              <p className="text-sm text-[#A0A0A0]">
                Expert review layer for diagnostic assessment
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-[#A0A0A0] hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Diagnostic Summary */}
          {diagnostic && (
            <div className="bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#A0A0A0]">Diagnostic ID:</span>
                  <span className="ml-2 font-mono text-white">{diagnosticId.slice(0, 8).toUpperCase()}</span>
                </div>
                <div>
                  <span className="text-[#A0A0A0]">AI Risk:</span>
                  <span className="ml-2 font-semibold text-[#FF6A00]">{diagnostic.overall_risk || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Rating Scales */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Assessment Framework</h3>
              
              <RatingSlider
                label="Evidence Bar"
                icon={Shield}
                value={judgment.evidence_bar}
                onChange={(val) => setJudgment({...judgment, evidence_bar: val})}
                description="Strength of evidence supporting this risk (1=weak, 5=strong)"
              />

              <RatingSlider
                label="Trust Impact"
                icon={TrendingUp}
                value={judgment.trust_impact}
                onChange={(val) => setJudgment({...judgment, trust_impact: val})}
                description="User trust erosion impact (1=minimal, 5=severe)"
              />

              <RatingSlider
                label="Reproducibility"
                icon={RefreshCw}
                value={judgment.reproducibility}
                onChange={(val) => setJudgment({...judgment, reproducibility: val})}
                description="Can this issue be replicated? (1=hard, 5=easy)"
              />

              <RatingSlider
                label="Actionability"
                icon={Wrench}
                value={judgment.actionability}
                onChange={(val) => setJudgment({...judgment, actionability: val})}
                description="Can this be fixed/addressed? (1=hard, 5=easy)"
              />

              <RatingSlider
                label="Confidence Level"
                icon={Target}
                value={judgment.confidence_level}
                onChange={(val) => setJudgment({...judgment, confidence_level: val})}
                description="Assessment confidence (1=low, 5=high)"
              />
            </div>

            {/* Severity Selection */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="w-4 h-4 text-[#FF6A00]" />
                Severity Discipline
              </label>
              <div className="grid grid-cols-4 gap-2">
                {['Critical', 'High', 'Medium', 'Low'].map((sev) => (
                  <button
                    key={sev}
                    onClick={() => setJudgment({...judgment, severity: sev})}
                    className={`px-4 py-2 rounded-md font-medium transition-all ${
                      judgment.severity === sev
                        ? 'bg-[#FF6A00] text-white'
                        : 'bg-[#1A1A1D] text-[#A0A0A0] hover:bg-[#1A1A1D]/80'
                    }`}
                  >
                    {sev}
                  </button>
                ))}
              </div>
            </div>

            {/* Decision Flags */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Commercial Judgment</label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 bg-[#1A1A1D] rounded-md cursor-pointer hover:bg-[#1A1A1D]/80 transition-colors">
                  <input
                    type="checkbox"
                    checked={judgment.is_credible}
                    onChange={(e) => setJudgment({...judgment, is_credible: e.target.checked})}
                    className="w-4 h-4 accent-[#FF6A00]"
                  />
                  <span className="text-sm">Credible</span>
                </label>

                <label className="flex items-center gap-2 p-3 bg-[#1A1A1D] rounded-md cursor-pointer hover:bg-[#1A1A1D]/80 transition-colors">
                  <input
                    type="checkbox"
                    checked={judgment.is_material}
                    onChange={(e) => setJudgment({...judgment, is_material: e.target.checked})}
                    className="w-4 h-4 accent-[#FF6A00]"
                  />
                  <span className="text-sm">Material</span>
                </label>

                <label className="flex items-center gap-2 p-3 bg-[#1A1A1D] rounded-md cursor-pointer hover:bg-[#1A1A1D]/80 transition-colors">
                  <input
                    type="checkbox"
                    checked={judgment.is_urgent}
                    onChange={(e) => setJudgment({...judgment, is_urgent: e.target.checked})}
                    className="w-4 h-4 accent-[#FF6A00]"
                  />
                  <span className="text-sm">Urgent</span>
                </label>

                <label className="flex items-center gap-2 p-3 bg-[#1A1A1D] rounded-md cursor-pointer hover:bg-[#1A1A1D]/80 transition-colors">
                  <input
                    type="checkbox"
                    checked={judgment.is_commercially_relevant}
                    onChange={(e) => setJudgment({...judgment, is_commercially_relevant: e.target.checked})}
                    className="w-4 h-4 accent-[#FF6A00]"
                  />
                  <span className="text-sm">Commercially Relevant</span>
                </label>
              </div>
            </div>

            {/* Judgment Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Expert Notes</label>
              <textarea
                value={judgment.judgment_notes}
                onChange={(e) => setJudgment({...judgment, judgment_notes: e.target.value})}
                rows={4}
                className="w-full px-4 py-3 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white placeholder-[#A0A0A0] focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                placeholder="Add contextual notes, override rationale, or additional insights..."
              />
            </div>

            {/* Reviewer */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Reviewed By</label>
              <input
                type="text"
                value={judgment.reviewed_by}
                onChange={(e) => setJudgment({...judgment, reviewed_by: e.target.value})}
                className="w-full px-4 py-2 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                placeholder="Operator name"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-transparent border border-[#FF6A00]/30 text-white rounded-md hover:bg-[#FF6A00]/10 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Judgment'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JudgmentMode;
